import { useEffect, useState } from "react";
import { Track } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { StatefulComment } from "App/common/useSpaceComments";
import { ViewingSpaceId } from "App/modules/viewingSpace";

/**
 * Comments live per (spaceId, trackId) pair, so a track shared into several
 * spaces has a SEPARATE thread in each. `viewingSpaceId` is the space the
 * track is being viewed FROM; `track.spaceId` is its ORIGIN, which for a
 * borrowed track is a different space — often one the viewer cannot even
 * read. Passing no `viewingSpaceId` keeps the old origin-scoped behaviour,
 * which is correct for every non-borrowed track.
 *
 * Callers must source `viewingSpaceId` from the DATA (a row-backed
 * `SpaceTrackTile.viewingSpaceId`), never from `useUrlContext()` inside this
 * hook: it renders inside `SpacePicker`, which is mounted persistently in
 * the nav sidebar and therefore outlives any particular route.
 */
export const useTrackComments = (
  track: Track,
  viewingSpaceId?: ViewingSpaceId,
) => {
  const [commentsLoading, setCommentsLoading] = useState<boolean>(true);
  const [fetchedComments, setFetchedComments] = useState<
    Record<string, StatefulComment[]>
  >({});

  const commentSpaceId = viewingSpaceId || track?.spaceId;

  useEffect(() => {
    let unmounted: boolean;

    if (!commentSpaceId || typeof track?.id === "undefined") {
      setCommentsLoading(false);
      return;
    }

    setCommentsLoading(true);
    const unsubscribeComments = highnote.watchComments({
      spaceId: commentSpaceId,
      trackId: track.id,
      onChange: (_comments: StatefulComment[]) => {
        if (unmounted) return;
        setFetchedComments((prev) => {
          const newComments = { ...prev };

          _comments.forEach((comment) => {
            const trackVersionId = comment.trackVersionIds[0];

            if (!newComments[trackVersionId]) {
              newComments[trackVersionId] = [];
            }

            newComments[trackVersionId] = [
              ...newComments[trackVersionId],
              comment,
            ];
          });

          return newComments;
        });
        setCommentsLoading(false);
      },
    });

    return () => {
      unmounted = true;
      unsubscribeComments();
      setCommentsLoading(false);
    };
    // `commentSpaceId`, NOT `track.spaceId` — this repo has no
    // react-hooks/exhaustive-deps rule, so nothing mechanical catches a
    // missing dep here. Leaving the origin in the deps would keep the
    // listener pinned to the first space it saw when a borrowed track's
    // viewing space changes.
  }, [commentSpaceId, track?.id]);

  return { fetchedComments, commentsLoading };
};
